import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { TableViewDialog, TableDialogFooter, TableDialogBtn } from "../style";
import { FormGroup, Label, Input, Button, Table, Alert } from "reactstrap";
import { updateOrderItemExtras, createOrderItemAdditionalJob, deleteOrderItemAdditionalJob } from "../../project/service";
import { deleteAttachment } from "../../product/service";

const ExtrasModal = ({
  orderItem,
  tableManager,
  projectId,
  groupId,
  mutateCurrentRoom,
  replaceModule,
  refreshProjectSummary,
}) => {
  const [viewMode, setViewMode] = useState("list");
  const [editingExtraId, setEditingExtraId] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null); // {component_uniq_id, component_label, component_type}
  const [selectedPanelIdentifier, setSelectedPanelIdentifier] = useState(null); // panel identifier
  const [category, setCategory] = useState("");
  const [cncType, setCncType] = useState("none");
  const [patternType, setPatternType] = useState("none");
  const [remarks, setRemarks] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [label, setLabel] = useState("");
  const [showAttachmentForm, setShowAttachmentForm] = useState(false);
  const hiddenFileInput = useRef(null);

  const [deletedExtraIds, setDeletedExtraIds] = useState(new Set());
  const [optimisticExtras, setOptimisticExtras] = useState(null);
  const [lastSavedExtraId, setLastSavedExtraId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [originalExtrasSnapshot, setOriginalExtrasSnapshot] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(null);

  const additionalJobs = orderItem?.additional_jobs || {};
  const allExtras = additionalJobs?.extras || [];
  const components = additionalJobs?.components || [];

  // Helper to compare panel_locate objects
  const panelLocateEquals = (locate1, locate2) => {
    if (!locate1 && !locate2) return true;
    if (!locate1 || !locate2) return false;
    return locate1.component_uniq_id === locate2.component_uniq_id &&
           locate1.panel_identifier === locate2.panel_identifier;
  };

  useEffect(() => {
    if (optimisticExtras && lastSavedExtraId) {
      const serverHasUpdate = allExtras.some(extra => {
        if (optimisticExtras.isUpdate) {
          return extra.id === optimisticExtras.id &&
                 panelLocateEquals(extra.panel_locate, optimisticExtras.panel_locate) &&
                 extra.category === optimisticExtras.category &&
                 extra.sub_category === optimisticExtras.sub_category &&
                 extra.remarks === optimisticExtras.remarks;
        } else {
          return panelLocateEquals(extra.panel_locate, optimisticExtras.panel_locate) &&
                 extra.category === optimisticExtras.category &&
                 extra.sub_category === optimisticExtras.sub_category &&
                 extra.remarks === optimisticExtras.remarks;
        }
      });

      if (serverHasUpdate) {
        setOptimisticExtras(null);
        setLastSavedExtraId(null);
        setOriginalExtrasSnapshot(null);
      }
    }
  }, [allExtras, optimisticExtras, lastSavedExtraId]);

  useEffect(() => {
    if (deletedExtraIds.size > 0) {
      deletedExtraIds.forEach(deletedId => {
        const stillExists = allExtras.some(extra => extra.id === deletedId);
        if (!stillExists) {
          setDeletedExtraIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(deletedId);
            return newSet;
          });
          setOriginalExtrasSnapshot(null);
        }
      });
    }
  }, [allExtras, deletedExtraIds]);

  let processedExtras = allExtras.filter(extra => !deletedExtraIds.has(extra.id));

  if (optimisticExtras) {
    if (optimisticExtras.isUpdate) {
      processedExtras = processedExtras.map(extra =>
        extra.id === optimisticExtras.id ? optimisticExtras : extra
      );
    } else {
      const alreadyExists = processedExtras.some(extra =>
        panelLocateEquals(extra.panel_locate, optimisticExtras.panel_locate) &&
        extra.category === optimisticExtras.category &&
        extra.sub_category === optimisticExtras.sub_category
      );
      if (!alreadyExists) {
        processedExtras = [...processedExtras, optimisticExtras];
      }
    }
  }

  const extras = processedExtras;

  const getCncTypeLabel = (value) => {
    if (!value || value === "none") return "None";
    const cncTypeMap = {
      "simple": "Pooja Bells",
      "complex": "Pooja Curve / Flower",
      "horizontal_air_vent_groove": "Horizontal Air Vent Groove",
      "glass_rectangle_cut": "Glass Rectangle Cut",
      "louver_fixing_rectangle_cut": "Louver Fixing Rectangle Cut",
      "wardrobe_cross_groove": "Wardrobe Cross Groove",
      "wardrobe_horizontal_groove": "Wardrobe Horizontal Groove",
      "wardrobe_vertical_groove": "Wardrobe Vertical Groove",
      "t_profile_groove": "T Profile Groove",
      "rectangle_cut_with_step": "Rectangle Cut with Step",
      "4mm_layer": "4mm Layer",
      "panel_curve_cut": "Panel Curve Cut",
      "edge_handle_cut": "Edge Handle Cut",
    };
    return cncTypeMap[value] || value;
  };

  const getPatternTypeLabel = (value) => {
    if (!value || value === "none") return "None";
    const patternTypeMap = {
      "simple": "Horizontal Center",
      "complex": "Zig Zag",
      "vertical_center": "Vertical Center",
      "vertical_corner": "Vertical Corner",
      "center_rectangle_with_border": "Center Rectangle with Border",
      "arch": "Arch",
      "horizontal_3_color": "Horizontal 3 Color",
      "horizontal_4_color": "Horizontal 4 Color",
    };
    return patternTypeMap[value] || value;
  };

  // Get component by uniq_id
  const getComponentByUniqId = (uniqId) => {
    return components.find(c => c.component_uniq_id === uniqId);
  };

  // Get panels for a component
  // panels is an array of strings (panel identifiers) like ["drawer_left_panel", "drawer_right_panel", ...]
  const getComponentPanels = (component) => {
    if (!component) return [];
    // Check if component has panels array directly
    if (component.panels && Array.isArray(component.panels) && component.panels.length > 0) {
      return component.panels;
    }
    // If no panels array, return empty - we'll use default panel identifier based on component_type
    return [];
  };

  const initializeForm = (extra = null) => {
    setErrorMessage(null);
    if (extra) {
      setEditingExtraId(extra.id);

      // Set component and panel from panel_locate
      // If panel_locate is null, it's Carcass
      if (extra.panel_locate) {
        const component = getComponentByUniqId(extra.panel_locate.component_uniq_id);
        setSelectedComponent(component || null);
        setSelectedPanelIdentifier(extra.panel_locate.panel_identifier || null);
      } else {
        // panel_locate is null means Carcass
        setSelectedComponent(null);
        setSelectedPanelIdentifier(null);
      }

      setRemarks(extra.remarks || "");

      const attachmentObjects = (orderItem?.attachments || []).filter(att => {
        return (extra.attachment_ids || []).some(attId => {
          return att.id === attId || att.blob_id === attId;
        });
      });

      setAttachments(attachmentObjects);

      setCategory(extra.category || "");
      if (extra.category === "cnc") {
        setCncType(extra.sub_category || "none");
        setPatternType("none");
      } else if (extra.category === "pattern") {
        setPatternType(extra.sub_category || "none");
        setCncType("none");
      } else {
        setCncType("none");
        setPatternType("none");
      }

      setPendingFiles([]);
      setDeletedAttachmentIds([]);
    } else {
      setEditingExtraId(null);
      setSelectedComponent(null);
      setSelectedPanelIdentifier(null);
      setCncType("none");
      setPatternType("none");
      setRemarks("");
      setAttachments([]);
      setCategory("");
      setPendingFiles([]);
      setDeletedAttachmentIds([]);
    }
    setLabel("");
    setShowAttachmentForm(false);
    setOriginalFormData({
      editingExtraId: extra?.id || null,
      selectedComponent: extra?.panel_locate ? getComponentByUniqId(extra.panel_locate.component_uniq_id) : null,
      selectedPanelIdentifier: extra?.panel_locate?.panel_identifier || null,
      cncType: extra?.category === "cnc" ? (extra?.sub_category || "none") : "none",
      patternType: extra?.category === "pattern" ? (extra?.sub_category || "none") : "none",
      remarks: extra?.remarks || "",
      attachments: (extra?.attachment_ids || []).map(attId => {
        const attachment = orderItem?.attachments?.find(a => a.id === attId || a.blob_id === attId);
        return attachment || attId;
      }),
      category: extra?.category || "",
      pendingFiles: [],
      deletedAttachmentIds: [],
    });
  };

  const handleEditClick = (extra) => {
    setErrorMessage(null);
    initializeForm(extra);
    setViewMode("form");
  };

  const handleAddJobClick = () => {
    setErrorMessage(null);
    initializeForm(null);
    setViewMode("form");
  };

  const handleDeleteClick = (extra) => {
    if (window.confirm("Are you sure you want to delete this additional job?")) {
      setOriginalExtrasSnapshot({
        extras: [...extras],
        deletedIds: new Set(deletedExtraIds),
        optimisticExtras: optimisticExtras,
      });
      setDeletedExtraIds(prev => new Set([...prev, extra.id]));

      deleteOrderItemAdditionalJob({
        projectId,
        groupId,
        orderItemId: orderItem.id,
        additional_job_id: extra.id,
      })
        .then((response) => {
          const updatedModule = response?.data || response;
          if (updatedModule && replaceModule) {
            replaceModule(updatedModule);
          }
          if (refreshProjectSummary) {
            refreshProjectSummary();
          }
          mutateCurrentRoom();
          setViewMode("list");
          setEditingExtraId(null);
        })
        .catch((error) => {
          setDeletedExtraIds(originalExtrasSnapshot.deletedIds);
          setOptimisticExtras(originalExtrasSnapshot.optimisticExtras);
          alert("Error deleting additional job. Please try again.");
        });
    }
  };

  const handleRevert = () => {
    if (originalFormData) {
      setEditingExtraId(originalFormData.editingExtraId);
      setSelectedComponent(originalFormData.selectedComponent);
      setSelectedPanelIdentifier(originalFormData.selectedPanelIdentifier);
      setCncType(originalFormData.cncType);
      setPatternType(originalFormData.patternType);
      setRemarks(originalFormData.remarks);
      setAttachments(originalFormData.attachments);
      setCategory(originalFormData.category);
      setPendingFiles(originalFormData.pendingFiles);
      setDeletedAttachmentIds(originalFormData.deletedAttachmentIds);
      setLabel("");
      setShowAttachmentForm(false);
      setErrorMessage(null);
    }
  };

  const handleComponentChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      const component = getComponentByUniqId(selectedOption.value);
      setSelectedComponent(component);

      // Get panels for this component (panels is an array of strings)
      const panels = getComponentPanels(component);

      // If only one panel, select it automatically
      if (panels.length === 1) {
        setSelectedPanelIdentifier(panels[0]); // panels[0] is already a string (panel identifier)
      } else if (panels.length > 1) {
        // Multiple panels - user will select from dropdown
        setSelectedPanelIdentifier(null);
      } else {
        // No panels array - use default panel identifier based on component_type
        // For shutters, default is "shutter"
        // For other types, use component_type as default
        if (component?.component_type === "shutter") {
          setSelectedPanelIdentifier("shutter");
        } else {
          // For other component types, use component_type as panel_identifier
          setSelectedPanelIdentifier(component?.component_type || null);
        }
      }
    } else {
      setSelectedComponent(null);
      setSelectedPanelIdentifier(null);
    }

    if (!editingExtraId) {
      setCategory("");
      setCncType("none");
      setPatternType("none");
      setRemarks("");
      setAttachments([]);
      setPendingFiles([]);
      setDeletedAttachmentIds([]);
    }
    setErrorMessage(null);
  };

  const handlePanelChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      setSelectedPanelIdentifier(selectedOption.value);
    } else {
      setSelectedPanelIdentifier(null);
    }
    setErrorMessage(null);
  };

  const cncTypeOptions = [
    { label: "None", value: "none" },
    { label: "Pooja Bells", value: "simple" },
    { label: "Pooja Curve / Flower", value: "complex" },
    { label: "Horizontal Air Vent Groove", value: "horizontal_air_vent_groove" },
    { label: "Glass Rectangle Cut", value: "glass_rectangle_cut" },
    { label: "Louver Fixing Rectangle Cut", value: "louver_fixing_rectangle_cut" },
    { label: "Wardrobe Cross Groove", value: "wardrobe_cross_groove" },
    { label: "Wardrobe Horizontal Groove", value: "wardrobe_horizontal_groove" },
    { label: "Wardrobe Vertical Groove", value: "wardrobe_vertical_groove" },
    { label: "T Profile Groove", value: "t_profile_groove" },
    { label: "Rectangle Cut with Step", value: "rectangle_cut_with_step" },
    { label: "4mm Layer", value: "four_mm_layer" },
    { label: "Panel Curve Cut", value: "panel_curve_cut" },
    { label: "Edge Handle Cut", value: "edge_handle_cut" },
  ];

  const patternTypeOptions = [
    { label: "None", value: "none" },
    { label: "Horizontal Center", value: "simple" },
    { label: "Zig Zag", value: "complex" },
    { label: "Vertical Center", value: "vertical_center" },
    { label: "Vertical Corner", value: "vertical_corner" },
    { label: "Center Rectangle with Border", value: "center_rectangle_with_border" },
    { label: "Arch", value: "arch" },
    { label: "Horizontal 3 Color", value: "horizontal_3_color" },
    { label: "Horizontal 4 Color", value: "horizontal_4_color" },
  ];

  // Generate component options from components array
  const componentOptions = components.map(component => ({
    label: component.component_type === 'carcass' ? 'Carcass' : `${component.component_label} (${component.component_type})`,
    value: component.component_uniq_id,
    component: component,
  }));

  // Get panels for selected component
  // panels is an array of strings (panel identifiers)
  const selectedComponentPanels = selectedComponent ? getComponentPanels(selectedComponent) : [];

  // Normalize panel identifier for display (convert underscores to spaces and capitalize words)
  const normalizePanelLabel = (identifier) => {
    return identifier
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const panelOptions = selectedComponentPanels.map(panelIdentifier => ({
    label: normalizePanelLabel(panelIdentifier), // Normalized label: "drawer_left_panel" -> "Drawer Left Panel"
    value: panelIdentifier, // Keep original value: "drawer_left_panel"
  }));

  // Get current selected component value for dropdown
  const getSelectedComponentValue = () => {
    if (selectedComponent) {
      return componentOptions.find(opt => opt.value === selectedComponent.component_uniq_id);
    }
    return null;
  };

  // Get current selected panel value for dropdown
  const getSelectedPanelValue = () => {
    if (selectedPanelIdentifier) {
      return panelOptions.find(opt => opt.value === selectedPanelIdentifier);
    }
    return null;
  };

  const handleFileSelect = (file) => {
    const fileName = file.name;
    const labelValue = label || fileName;
    setPendingFiles(prev => [...prev, { file, label: labelValue }]);
    setLabel("");
    setShowAttachmentForm(false);
    setErrorMessage(null);
  };

  const handleDeletePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileNameWithoutExtension = (fileName) => {
    if (!fileName) return "";
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return "";
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : "";
  };

  const getAttachmentDisplayName = (attachment) => {
    if (attachment.label && attachment.label.trim()) {
      // If label already includes extension, return as is
      return attachment.label;
    }
    return attachment.filename || attachment.name || "Attachment";
  };

  const hasRemarks = remarks && remarks.trim().length > 0;
  const hasAttachments = (attachments && attachments.length > 0) || pendingFiles.length > 0;
  const hasRemarksOrAttachments = hasRemarks || hasAttachments;

  let isValid = true;
  if (category === "cnc") {
    isValid = cncType !== "none" && hasRemarksOrAttachments;
  } else if (category === "pattern") {
    isValid = patternType !== "none" && hasRemarksOrAttachments;
  } else if (category === "other") {
    isValid = hasRemarksOrAttachments;
  } else {
    isValid = false;
  }

  const handleSave = async () => {
    setErrorMessage(null);

    if (!isValid) {
      if (category === "cnc") {
        alert("CNC Type and Remarks or Attachments are required.");
      } else if (category === "pattern") {
        alert("Pattern Type and Remarks or Attachments are required.");
      } else if (category === "other") {
        alert("Remarks or Attachments are required.");
      } else {
        alert("Please select a Category.");
      }
      return;
    }

    if (deletedAttachmentIds.length > 0) {
      try {
        const deletePromises = deletedAttachmentIds.map(blobId =>
          deleteAttachment({
            projectId,
            groupId,
            orderItemId: orderItem.id,
            blobId,
          })
        );

        await Promise.all(deletePromises);
        setDeletedAttachmentIds([]);
      } catch (error) {
        const errorMsg = error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error?.message ||
                        "Error deleting attachments. Please try again.";
        setErrorMessage(errorMsg);
        return;
      }
    }

    const existingAttachmentIds = attachments.map(att => {
      if (typeof att === 'number') return att;
      if (att.id) return att.id;
      if (att.blob_id) return att.blob_id;
      return att;
    }).filter(id => id !== null && id !== undefined);

    const attachmentIds = existingAttachmentIds;

    // Build panel_locate - null if no component selected, otherwise hash with component_uniq_id and panel_identifier
    let panelLocate = null;
    if (selectedComponent && selectedPanelIdentifier) {
      panelLocate = {
        component_uniq_id: selectedComponent.component_uniq_id,
        panel_identifier: selectedPanelIdentifier,
      };
    }

    const isUpdating = !!editingExtraId;
    const currentEditingId = editingExtraId;

    let subCategory = undefined;
    if (category === "cnc") {
      subCategory = cncType === "none" ? undefined : cncType;
    } else if (category === "pattern") {
      subCategory = patternType === "none" ? undefined : patternType;
    }

    const attachmentFiles = pendingFiles.length > 0
      ? pendingFiles.map(({ file, label }) => {
          // If label is not provided, use filename without extension
          const fileName = file.name;
          const nameWithoutExt = getFileNameWithoutExtension(fileName);
          // Remove extension from label if it already has one
          const labelWithoutExt = label ? getFileNameWithoutExtension(label) : null;
          return {
            file: file,
            label: labelWithoutExt || nameWithoutExt,
          };
        })
      : undefined;

    setOriginalExtrasSnapshot({
      extras: [...extras],
      deletedIds: new Set(deletedExtraIds),
      optimisticExtras: optimisticExtras,
    });

    const optimisticPendingAttachments = pendingFiles.map(({ file, label }) => ({
      id: `pending-${Date.now()}-${Math.random()}`,
      filename: file.name,
      label: label || file.name,
      url: URL.createObjectURL(file),
      isPending: true,
    }));

    const optimisticExtra = {
      id: currentEditingId || Date.now(),
      panel_locate: panelLocate,
      category: category || null,
      sub_category: subCategory !== undefined ? subCategory : null,
      remarks: remarks || null,
      attachment_ids: attachmentIds,
      optimistic_attachments: optimisticPendingAttachments,
    };

    setPendingFiles([]);

    setOptimisticExtras({
      ...optimisticExtra,
      isUpdate: isUpdating,
    });

    setViewMode("list");
    setEditingExtraId(null);

    const apiCall = isUpdating
      ? updateOrderItemExtras({
          projectId,
          groupId,
          orderItemId: orderItem.id,
          additional_job_id: currentEditingId,
          panel_locate: panelLocate,
          category: category || undefined,
          sub_category: subCategory,
          remarks: remarks || undefined,
          attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
          attachments: attachmentFiles,
        })
      : createOrderItemAdditionalJob({
          projectId,
          groupId,
          orderItemId: orderItem.id,
          panel_locate: panelLocate,
          category: category || undefined,
          sub_category: subCategory,
          remarks: remarks || undefined,
          attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
          attachments: attachmentFiles,
        });

    apiCall
      .then((response) => {
        const updatedModule = response?.data || response;
        if (updatedModule && replaceModule) {
          replaceModule(updatedModule);
        }
        if (refreshProjectSummary) {
          refreshProjectSummary();
        }

        const savedExtra = updatedModule?.additional_jobs?.extras?.find(extra => {
          if (isUpdating) {
            return extra.id === currentEditingId;
          } else {
            return extra.id === optimisticExtra.id ||
                   panelLocateEquals(extra.panel_locate, panelLocate);
          }
        });

        if (savedExtra) {
          setLastSavedExtraId(savedExtra.id);
        }

        mutateCurrentRoom();
      })
      .catch((error) => {
        setOptimisticExtras(originalExtrasSnapshot.optimisticExtras);
        setLastSavedExtraId(null);

        const errorMsg = error?.response?.data?.error ||
                        error?.response?.data?.message ||
                        error?.message ||
                        "Error saving additional job. Please try again.";

        setErrorMessage(errorMsg);
        setViewMode("form");
        setEditingExtraId(currentEditingId);
      });
  };

  const renderListView = () => {
    return (
      <>
        <div className="d-flex flex-column">
          {extras.length > 0 ? (
            <Table bordered size="sm" className="bg-light-grey">
              <thead className="bg-secondary text-white">
                <tr>
                  <th className="bg-secondary text-white">Panel</th>
                  <th className="bg-secondary text-white">Work</th>
                  <th className="bg-secondary text-white">Detail</th>
                  <th className="bg-secondary text-white">Attachments</th>
                  <th className="bg-secondary text-white">Remarks</th>
                  <th className="bg-secondary text-white" style={{ width: "60px" }}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-light-grey">
                {extras.map((extra) => {
                  let typeLabel = "-";
                  if (extra.panel_locate) {
                    const component = getComponentByUniqId(extra.panel_locate.component_uniq_id);
                    typeLabel = component?.component_label || "Unknown";
                  }

                  const getSubCategoryLabel = (category, value) => {
                    if (!value || value === "none") return "None";
                    if (category === "cnc") {
                      return getCncTypeLabel(value);
                    } else if (category === "pattern") {
                      return getPatternTypeLabel(value);
                    }
                    return value;
                  };

                  return (
                    <tr key={extra.id} className="bg-light-grey">
                      <td className="bg-light-grey">{typeLabel}</td>
                      <td className="bg-light-grey">{extra.category ? extra.category.charAt(0).toUpperCase() + extra.category.slice(1) : "-"}</td>
                      <td className="bg-light-grey">{extra.sub_category ? getSubCategoryLabel(extra.category, extra.sub_category) : "-"}</td>
                      <td className="bg-light-grey">
                        {(() => {
                          const optimisticAttachments = extra.optimistic_attachments || [];

                          const existingAttachments = (extra.attachment_ids || []).map(attId => {
                            const attachment = orderItem?.attachments?.find(att =>
                              att.id === attId || att.blob_id === attId || att === attId
                            );
                            return attachment;
                          }).filter(Boolean);

                          const allAttachments = [...existingAttachments, ...optimisticAttachments];

                          if (allAttachments.length === 0) return "-";

                          return (
                            <div>
                              {allAttachments.map((attachment, idx) => {
                                const label = attachment?.label;
                                const filename = attachment?.filename || attachment?.name || `Attachment ${idx + 1}`;
                                const displayText = label && label.trim() ? label : filename;

                                return (
                                  <div key={idx} className="small">
                                    {displayText}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="bg-light-grey">{extra.remarks || "-"}</td>
                      <td className="bg-light-grey">
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="primary"
                            onClick={() => handleEditClick(extra)}
                            style={{ padding: "2px 8px" }}
                          >
                            <i className="fa-solid fa-pencil"></i>
                          </Button>
                          <Button
                            size="sm"
                            outline
                            color="danger"
                            onClick={() => handleDeleteClick(extra)}
                            style={{ padding: "2px 8px" }}
                            title="Delete additional job"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-3 text-muted">
              CNC, Pattern or other works
            </div>
          )}
          <div
            className="link pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleAddJobClick();
            }}
            style={{color: "#2C92E6" }}
          >
            + Add Work
          </div>
        </div>
        <TableDialogFooter>
          <TableDialogBtn
            onClick={(e) => {
              e.stopPropagation();
              tableManager.clear();
            }}
            className="mt-3"
          >
            Close
          </TableDialogBtn>
        </TableDialogFooter>
      </>
    );
  };

  const renderFormView = () => {
    const isEditing = !!editingExtraId;
    // Show panel dropdown only if component is selected (not Carcass) and has more than one panel
    const showPanelDropdown = selectedComponent && selectedComponentPanels.length > 1;

    return (
      <>
        <div className="d-flex flex-column row-gap-3">
        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        <div>
          <p className="grey_letter mb-1">Panel</p>
          <div className="column-gap-3 d-flex align-items-center">
            <Select
              options={componentOptions}
              value={getSelectedComponentValue()}
              onChange={handleComponentChange}
              className="list-summary-select"
              isDisabled={isEditing}
            />
          </div>
        </div>

        {showPanelDropdown && (
          <div>
            <div className="column-gap-3 d-flex align-items-center">
              <Select
                options={panelOptions}
                value={getSelectedPanelValue()}
                onChange={handlePanelChange}
                className="list-summary-select"
                isDisabled={isEditing}
                placeholder={ `Select Panel within ${getSelectedComponentValue()?.label}` }
              />
            </div>
          </div>
        )}

        <div>
          <p className="grey_letter mb-1">Category</p>
          <div className="column-gap-3 d-flex align-items-center">
            <Select
              options={[
                { label: "Select Category", value: "" },
                { label: "CNC", value: "cnc" },
                { label: "Pattern", value: "pattern" },
                { label: "Other", value: "other" },
              ]}
              value={category ? { label: category.charAt(0).toUpperCase() + category.slice(1), value: category } : null}
              onChange={(e) => {
                setCategory(e.value);
                if (e.value !== "cnc") setCncType("none");
                if (e.value !== "pattern") setPatternType("none");
                setErrorMessage(null);
              }}
              className="list-summary-select"
              isDisabled={isEditing}
            />
          </div>
        </div>

        {category === "cnc" && (
          <div>
            <p className="grey_letter mb-1">CNC Type</p>
            <div className="column-gap-3 d-flex align-items-center">
              <Select
                options={cncTypeOptions}
                value={cncTypeOptions.find(opt => opt.value === cncType)}
                onChange={(e) => setCncType(e.value)}
                className="list-summary-select"
                isDisabled={false}
              />
            </div>
          </div>
        )}

        {category === "pattern" && (
          <div>
            <p className="grey_letter mb-1">Pattern Type</p>
            <div className="column-gap-3 d-flex align-items-center">
              <Select
                options={patternTypeOptions}
                value={patternTypeOptions.find(opt => opt.value === patternType)}
                onChange={(e) => setPatternType(e.value)}
                className="list-summary-select"
                isDisabled={false}
              />
            </div>
          </div>
        )}

        <div>
          <p className="grey_letter mb-1">Remarks</p>
          <div className="column-gap-3 d-flex align-items-center">
            <Input
              type="textarea"
              rows="4"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks here..."
            />
          </div>
        </div>

        <div>
          {(attachments.length > 0 || pendingFiles.length > 0) ? (
            <div className="border p-2 rounded">
              <p className="grey_letter mb-2 fw-bold">Attachments</p>
              <Table size="sm" className="mb-2" style={{ border: "none", background: "transparent" }}>
                <tbody style={{ border: "none", background: "transparent" }}>
                  {attachments.map((attachment, index) => {
                    const displayName = getAttachmentDisplayName(attachment);
                    const nameWithoutExt = getFileNameWithoutExtension(displayName);
                    const fileName = attachment.filename || attachment.name || "";
                    const extension = getFileExtension(fileName);
                    return (
                      <tr key={attachment.id || attachment.blob_id || index} style={{ border: "none" }}>
                        <td style={{ border: "none", background: "transparent" }}>
                          {nameWithoutExt}{extension && `.${extension}`}
                        </td>
                        <td style={{ border: "none", background: "transparent" }}>
                          <Button
                            size="sm"
                            outline
                            color="danger"
                            onClick={() => {
                              setDeletedAttachmentIds(prev => [...prev, attachment.id || attachment.blob_id]);
                              setAttachments(prev => prev.filter(att => 
                                att.id !== (attachment.id || attachment.blob_id) && 
                                att.blob_id !== (attachment.id || attachment.blob_id)
                              ));
                            }}
                            style={{ padding: "2px 8px" }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {pendingFiles.map((pending, index) => {
                    const fileName = pending.file.name;
                    const displayName = pending.label || fileName;
                    const nameWithoutExt = getFileNameWithoutExtension(displayName);
                    const extension = getFileExtension(fileName);
                    return (
                      <tr key={`pending-${index}`} style={{ border: "none" }}>
                        <td style={{ border: "none", background: "transparent" }}>
                          {nameWithoutExt}{extension && `.${extension}`}
                        </td>
                        <td style={{ border: "none", background: "transparent" }}>
                          <Button
                            size="sm"
                            outline
                            color="danger"
                            onClick={() => handleDeletePendingFile(index)}
                            style={{ padding: "2px 8px" }}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {showAttachmentForm ? (
                <div className="p-2 mb-2">
                  <FormGroup className="d-flex gap-2">
                    <Input
                      type="file"
                      innerRef={hiddenFileInput}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          const fileName = file.name;
                          setLabel(fileName);
                          handleFileSelect(file);
                        }
                      }}
                      style={{ display: "none" }}
                    />
                    <Button
                      size="sm"
                      onClick={() => hiddenFileInput.current?.click()}
                    >
                      Choose File
                    </Button>
                    <Button
                      size="sm"
                      outline
                      onClick={() => {
                        setShowAttachmentForm(false);
                        setLabel("");
                      }}
                    >
                      Cancel
                    </Button>
                  </FormGroup>
                </div>
              ) : (
                <div
                  className="link pointer py-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAttachmentForm(true);
                  }}
                  style={{ color: "#2C92E6" }}
                >
                  <i className="fa-solid fa-plus me-1"></i> Add
                </div>
              )}
            </div>
          ) : (
            <>
              {showAttachmentForm ? (
                <div className="border p-2 mb-2 rounded">
                  <FormGroup className="d-flex gap-2">
                    <Input
                      type="file"
                      innerRef={hiddenFileInput}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          const fileName = file.name;
                          setLabel(fileName);
                          handleFileSelect(file);
                        }
                      }}
                      style={{ display: "none" }}
                    />
                    <Button
                      size="sm"
                      onClick={() => hiddenFileInput.current?.click()}
                    >
                      Choose File
                    </Button>
                    <Button
                      size="sm"
                      outline
                      onClick={() => {
                        setShowAttachmentForm(false);
                        setLabel("");
                      }}
                    >
                      Cancel
                    </Button>
                  </FormGroup>
                </div>
              ) : (
                <Button
                  size="sm"
                  outline
                  onClick={() => setShowAttachmentForm(true)}
                >
                  <i className="fa-solid fa-circle-plus me-2"></i>
                  Add attachment
                </Button>
              )}
            </>
          )}
        </div>
        </div>
        <TableDialogFooter>
          <TableDialogBtn
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            disabled={!isValid}
            style={{
              opacity: !isValid ? 0.5 : 1,
              cursor: !isValid ? "not-allowed" : "pointer",
            }}
            className="mt-3"
          >
            Save
          </TableDialogBtn>
          <TableDialogBtn
            onClick={(e) => {
              e.stopPropagation();
              tableManager.clear();
            }}
            outline
            className="mt-3 ms-3"
          >
            Cancel
          </TableDialogBtn>
        </TableDialogFooter>
      </>
    );
  };

  return (
    <TableViewDialog
      className="end-0"
      end="true"
      onClick={(e) => e.stopPropagation()}
    >
      {viewMode === "list" ? renderListView() : renderFormView()}
    </TableViewDialog>
  );
};

export default ExtrasModal;
